import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PENDING_ACCOUNTS_KEY } from '../config/openAccountsPendingState';

export interface PendingAccount {
  accountNumber: string;
  accountType: 'spot' | 'invest' | 'margin' | 'earn';
  timeoutSeconds: number;
  assetCode?: string;
  startTime: number;
  remainingSeconds: number;
}

interface PendingAccountsState {
  pendingAccounts: PendingAccount[];
  addPendingAccount: (
    accountNumber: string,
    accountType: 'spot' | 'invest' | 'margin' | 'earn',
    timeoutSeconds: number,
    assetCode?: string
  ) => void;
  removePendingAccount: (accountNumber: string) => void;
  updateRemainingTime: () => void;
  clearExpiredAccounts: () => void;
}

export const usePendingAccountsStore = create<PendingAccountsState>()(
  persist(
    set => ({
      pendingAccounts: [],

      addPendingAccount: (
        accountNumber: string,
        accountType: 'spot' | 'invest' | 'margin' | 'earn',
        timeoutSeconds: number,
        assetCode?: string
      ) => {
        const startTime = Date.now();

        set(state => {
          // Remove existing account with same number if it exists
          const filteredAccounts = state.pendingAccounts.filter(
            acc => acc.accountNumber !== accountNumber
          );

          // Add new account
          const newAccount: PendingAccount = {
            accountNumber,
            accountType,
            timeoutSeconds,
            assetCode,
            startTime,
            remainingSeconds: timeoutSeconds,
          };

          const updatedAccounts = [...filteredAccounts, newAccount];

          return {
            pendingAccounts: updatedAccounts,
          };
        });
      },

      removePendingAccount: (accountNumber: string) => {
        set(state => ({
          pendingAccounts: state.pendingAccounts.filter(
            acc => acc.accountNumber !== accountNumber
          ),
        }));
      },

      updateRemainingTime: () => {
        set(state => {
          const now = Date.now();
          const updatedAccounts = state.pendingAccounts
            .map(account => {
              const elapsed = Math.floor((now - account.startTime) / 1000);
              const remaining = Math.max(0, account.timeoutSeconds - elapsed);
              return { ...account, remainingSeconds: remaining };
            })
            .filter(account => account.remainingSeconds > 0);

          return {
            pendingAccounts: updatedAccounts,
          };
        });
      },

      clearExpiredAccounts: () => {
        set(state => {
          const now = Date.now();
          const activeAccounts = state.pendingAccounts.filter(account => {
            const elapsed = Math.floor((now - account.startTime) / 1000);
            return account.timeoutSeconds - elapsed > 0;
          });

          return {
            pendingAccounts: activeAccounts,
          };
        });
      },
    }),
    {
      name: PENDING_ACCOUNTS_KEY,
    }
  )
);
